import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Box, 
  Typography, 
  FormControlLabel, 
  Checkbox, 
  Alert,
  Link,
  Collapse,
  Button
} from '@mui/material';
import PolicyIcon from '@mui/icons-material/Policy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DynamicESignatureDisclosure from './DynamicESignatureDisclosure';

const CombinedLegalCompliance = ({ 
  onComplianceChange, 
  apiRef, 
  disabled = false 
}) => {
  // E-signature consent states (from DynamicESignatureDisclosure)
  const [eSignatureConsents, setESignatureConsents] = useState({
    hardwareConfirmed: false,
    disclosureAcknowledged: false,
    withdrawalRightUnderstood: false,
    esignatureConsent: false,
    allConsentsGiven: false
  });

  // Platform terms acceptance
  const [platformTermsAccepted, setPlatformTermsAccepted] = useState(false);
  
  // Expanded states for compact view
  const [platformTermsExpanded, setPlatformTermsExpanded] = useState(false);

  // âœ… CRITICAL FIX: Use useRef to track if we've called the callback to prevent loops
  const onComplianceChangeRef = useRef(onComplianceChange);
  onComplianceChangeRef.current = onComplianceChange;

  // âœ… CRITICAL FIX: Remove onComplianceChange from dependency array to prevent infinite loop
  useEffect(() => {
    const allComplianceComplete = eSignatureConsents.allConsentsGiven && platformTermsAccepted;
    
    if (onComplianceChangeRef.current) {
      onComplianceChangeRef.current({
        ...eSignatureConsents,
        platformTermsAccepted,
        allComplianceComplete
      });
    }
  }, [eSignatureConsents, platformTermsAccepted]); // âœ… Removed onComplianceChange from deps

  const handleESignatureConsentChange = (consents) => {
    setESignatureConsents(consents);
  };

  const handlePlatformTermsChange = (event) => {
    setPlatformTermsAccepted(event.target.checked);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* E-Signature Disclosure Section */}
      <DynamicESignatureDisclosure
        onConsentChange={handleESignatureConsentChange}
        apiRef={apiRef}
        disabled={disabled}
      />

      {/* Platform Terms Section - Compact */}
      <Card variant="outlined" sx={{ mt: 1, p: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PolicyIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
            <Typography variant="subtitle2" sx={{ color: 'primary.main', fontSize: '0.9rem' }}>
              Platform Terms & Conditions
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setPlatformTermsExpanded(!platformTermsExpanded)}
            endIcon={platformTermsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.75rem' }}
          >
            Details
          </Button>
        </Box>
        
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: '0.8rem' }}>
          By using InkedUp Manager, you agree to our platform terms and disclaimers.
        </Typography>
        
        <Collapse in={platformTermsExpanded}>
          <Box sx={{ 
            mb: 2, 
            p: 1.5, 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 1, 
            backgroundColor: 'action.hover'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.75rem' }}>
              Key Platform Disclaimers:
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
              â€¢ InkedUp Manager facilitates electronic document signing only
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
              â€¢ Platform is not responsible for tattoo/piercing services
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
              â€¢ All procedure liability rests with shop/artist, not platform
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
              â€¢ Platform provides document management tools only
            </Typography>
          </Box>
        </Collapse>

        <FormControlLabel
          control={
            <Checkbox
              checked={platformTermsAccepted}
              onChange={handlePlatformTermsChange}
              color="primary"
              disabled={disabled}
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              I agree to the{' '}
              <Link 
                href="/terms-and-conditions" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'underline' }}
              >
                platform terms
              </Link>
              {' '}and disclaimers
            </Typography>
          }
          sx={{ mb: 0 }}
        />
      </Card>

      {/* Overall Compliance Status - Compact */}
      {eSignatureConsents.allConsentsGiven && platformTermsAccepted && (
        <Alert severity="success" sx={{ mt: 1, py: 1 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            âœ“ Legal requirements complete. Electronic signature enabled.
          </Typography>
        </Alert>
      )}

      {(!eSignatureConsents.allConsentsGiven || !platformTermsAccepted) && !disabled && (
        <Alert severity="warning" sx={{ mt: 1, py: 1 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            Complete both sections above to proceed with signing.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default CombinedLegalCompliance;